import React, { useRef, useState } from 'react';
import { ImagePlusIcon, Loader2Icon, LinkIcon, Trash2Icon } from 'lucide-react';
import { imageFileToDataUrl } from '../../utils/image';
import { FieldLabel } from './ui';
interface ImageInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** CSS object-position, e.g. "center 70%". */
  focus?: string;
  onFocusChange?: (focus: string | undefined) => void;
  aspect?: string;
}
const FOCUS_OPTIONS: {
  label: string;
  value: string | undefined;
}[] = [{
  label: 'Övre',
  value: 'center 25%'
}, {
  label: 'Mitten',
  value: undefined
}, {
  label: 'Nedre',
  value: 'center 75%'
}];

/** Upload (click or drop), or paste a link. Images are downsized before storing. */
export function ImageInput({
  label,
  value,
  onChange,
  focus,
  onFocusChange,
  aspect = 'aspect-[4/3]'
}: ImageInputProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await imageFileToDataUrl(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Uppladdningen misslyckades.');
    } finally {
      setBusy(false);
    }
  };
  return <div data-id="element-90" data-mp="1k46dgq0x7z0h">
      <FieldLabel data-id="element-91" data-mp="1k7u5fkqpwdd3">{label}</FieldLabel>
      <div onDragOver={e => {
      e.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={e => {
      e.preventDefault();
      setDragging(false);
      handleFile(e.dataTransfer.files[0]);
    }} className={`group relative ${aspect} w-full overflow-hidden rounded-2xl border-2 border-dashed transition-colors duration-150 ease-out ${dragging ? 'border-black bg-gray-100' : value ? 'border-transparent' : 'border-gray-200 bg-gray-50'}`} data-id="element-92" data-mp="1f6wlb2m5d98y">
        {value ? <img src={value} alt="" style={focus ? {
        objectPosition: focus
      } : undefined} className="h-full w-full object-cover" data-id="element-93" data-mp="1yue8qtymayqi" /> : <button type="button" onClick={() => fileRef.current?.click()} className="flex h-full w-full flex-col items-center justify-center gap-2 text-sm text-gray-500" data-id="element-94" data-mp="27vu4pwooqyb7">
            <ImagePlusIcon className="h-7 w-7 text-gray-400" data-id="element-95" data-mp="2hsqjgk56ggo7" />
            <span className="font-medium text-gray-700" data-id="element-96" data-mp="2mk8gwy7zrz4f">Ladda upp bild</span>
            <span className="text-xs" data-id="element-97" data-mp="2mmbhudaoncnx">eller dra och släpp här</span>
          </button>}

        {busy && <div className="absolute inset-0 flex items-center justify-center bg-white/80" data-id="element-98" data-mp="2rl4njjjq9zdj">
            <Loader2Icon className="h-6 w-6 animate-spin text-gray-700" data-id="element-99" data-mp="2vj298ceai3c6" />
          </div>}

        {value && !busy && <div className="absolute inset-x-3 bottom-3 flex gap-2" data-id="element-100" data-mp="ox5zx43mcft5">
            <button type="button" onClick={() => fileRef.current?.click()} className="rounded-full bg-white px-4 py-2 text-xs font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-100" data-id="element-101" data-mp="oua17djy5n3n">
              Byt bild
            </button>
            <button type="button" onClick={() => onChange('')} aria-label="Ta bort bild" className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-100" data-id="element-102" data-mp="az7yezaecdv2">
              <Trash2Icon className="h-3.5 w-3.5" data-id="element-103" data-mp="ysprxff2aucm" />
            </button>
          </div>}
      </div>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => {
      handleFile(e.target.files?.[0]);
      e.target.value = '';
    }} data-id="element-104" data-mp="ttwo6epkuhpn" />

      {error && <p className="mt-2 text-xs text-red-600" data-id="element-105" data-mp="164mc51skymm6">{error}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-106" data-mp="1678k32jdv44x">
        {onFocusChange && value && <div className="flex items-center gap-1.5 text-xs" data-id="element-107" data-mp="x4h5wuz4hu9l">
            <span className="text-gray-500" data-id="element-108" data-mp="x61zah9pb75v">Beskärning:</span>
            {FOCUS_OPTIONS.map(o => <button key={o.label} type="button" onClick={() => onFocusChange(o.value)} className={`rounded-full px-2.5 py-1 transition-colors ${focus === o.value ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-109" data-mp="123k9sc3d9ufz">
                {o.label}
              </button>)}
          </div>}
        <button type="button" onClick={() => setShowLink(s => !s)} className="flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-gray-900" data-id="element-110" data-mp="28r7g75cf6gky">
          <LinkIcon className="h-3.5 w-3.5" data-id="element-111" data-mp="2mwhiennp14fj" /> Använd länk
        </button>
      </div>

      {showLink && <input type="url" placeholder="https://…" defaultValue={value.startsWith('data:') ? '' : value} onBlur={e => e.target.value && onChange(e.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none transition-colors focus:border-black" data-id="element-112" data-mp="2iuminnleuz3v" />}
    </div>;
}